import { type MouseEvent } from 'react'

import { useTranslation } from 'react-i18next'

import { AlertDialog } from '@/components/alert-dialog'
import { Icon } from '@/components/icon'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui'
import {
  SimulacaoContentProvider,
  useSimulacaoContentContext,
} from '@/features/simulador/brasil/context/simulacao-content.context'
import { useBaixarPDFSimulacao, useGetSimulacaoDuplicar } from '@/features/simulador/brasil/hooks'
import { useValidarLancarProposta } from '@/features/simulador/brasil/hooks/use-validar-lancar-proposta'
import type { TGetSimulacaoDuplicarResponse } from '@/features/simulador/brasil/services/get-simulacao-duplicar.service'
import { cn } from '@/infra'

import { EmpreendimentoSection } from './empreendimento-section'
import { ValoresSimuladosSection } from './valores-simulados-section'

type TSimulacaoContentHeaderProps = { onDuplicarSimulacao: (simulacao: TGetSimulacaoDuplicarResponse) => void }

const Header = ({ onDuplicarSimulacao }: TSimulacaoContentHeaderProps) => {
  const { t } = useTranslation('simulacao')
  const { simulacao, handleIniciarAtendimento } = useSimulacaoContentContext()

  const { mutateAsync: baixarPdf, isPending: baixandoPdf } = useBaixarPDFSimulacao()
  const { mutateAsync: getSimulacaoDuplicar, isPending: duplicandoSimulacao } = useGetSimulacaoDuplicar()

  const { handleValidarLancarProposta } = useValidarLancarProposta({
    simulacaoId: simulacao.id,
    nomeEmpreendimento: simulacao?.empreendimento.nome,
    tipoDoProduto: simulacao.produto.tipoDoProduto,
    handleIniciarAtendimento: async () => handleIniciarAtendimento?.(simulacao.cliente.id),
  })

  const downloadPdfSimulacao =
    (simulacaoId: string) => async (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.preventDefault()
      await baixarPdf(simulacaoId)
    }

  const confirmarDuplicarSimulacaoDialog = async () =>
    await AlertDialog.show({
      title: t('modais.duplicar_simulacao.titulo'),
      description: t('modais.duplicar_simulacao.descricao'),
      actions: [
        { title: t('modais.duplicar_simulacao.voltar'), value: 'cancel', variant: 'outline' },
        { title: t('modais.duplicar_simulacao.confirmar'), value: 'confirm', variant: 'default' },
      ],
    })

  const handleDuplicarSimulacao = async (simulacaoId: string) => {
    const { value } = await confirmarDuplicarSimulacaoDialog()
    if (value === 'cancel') return

    const simulacaoDuplicada = await getSimulacaoDuplicar(simulacaoId)
    onDuplicarSimulacao(simulacaoDuplicada)
  }

  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="flex flex-col gap-4">
        <h2 className="font-poppins text-base leading-none font-normal tracking-wider uppercase">
          {t('pages.simulacao.simulacao_salva')}
        </h2>
        <div className="font-nunito flex flex-col gap-1 space-x-2 font-medium text-neutral-500 md:flex-row md:gap-0 md:divide-x [&>*]:pr-2 [&>*]:text-sm">
          <span>{t('pages.simulacao.rotinas', { rotinas: simulacao.rotinaArquivo.rotina })}</span>
          <span>{t('pages.simulacao.arquivo', { arquivo: simulacao.rotinaArquivo.arquivo })}</span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="group hidden md:inline-flex">
          <Button className="font-nunito px-2.5">
            <Icon name="chevron-down" className="transition-transform group-data-[state=open]:rotate-180" />
            {t('pages.simulacao.abrir_acoes')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          sideOffset={2}
          className={cn(
            'font-nunito w-56 p-2 text-sm',
            '[&>[role="menuitem"]]:h-12',
            '[&>[role="menuitem"]]:font-semibold',
            '[&>[role="menuitem"]]:text-sky-700',
            '[&>[role="menuitem"]]:hover:!text-sky-900',
          )}
          align="end"
        >
          <DropdownMenuLabel className="font-nunito text-base font-semibold tracking-wide">
            {t('pages.simulacao.acoes.titulo')}
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={handleValidarLancarProposta}>
            <Icon name="eye-open" className="text-sky-700" />
            {t('pages.simulacao.acoes.lancar_proposta')}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={duplicandoSimulacao} onClick={() => handleDuplicarSimulacao(simulacao.id)}>
            <Icon name="files" className="text-sky-700" />
            {t('pages.simulacao.acoes.duplicar_simulacao')}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={baixandoPdf} onClick={downloadPdfSimulacao(simulacao.id)}>
            <Icon
              name={baixandoPdf ? 'loader' : 'download'}
              className={cn('text-sky-700', { 'animate-spin': baixandoPdf })}
            />
            {baixandoPdf ? t('pages.simulacao.acoes.baixando_pdf') : t('pages.simulacao.acoes.baixar_pdf')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="fixed right-0 bottom-0 left-0 z-50 inline-flex border-t border-gray-200 bg-white px-6 py-4 md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button className="flex w-full items-center gap-2">
              <Icon name="chevron-up" size={16} />
              {t('pages.simulacao.abrir_acoes')}
            </Button>
          </SheetTrigger>

          <SheetContent
            side="bottom"
            className="w-full gap-0 rounded-t-xl border-t border-gray-200 bg-white p-0"
            aria-describedby={undefined}
          >
            <SheetHeader className="p-8" />
            <SheetTitle className="font-poppins px-6 py-4 text-lg leading-none font-normal tracking-normal text-wrap">
              {t('pages.simulacao.breadcrumb.simulacao_salva')}
            </SheetTitle>

            <div className="space-y-3 px-6 pb-8">
              <Button
                variant="ghost"
                onClick={handleValidarLancarProposta}
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 font-semibold"
              >
                <Icon name="send" size={16} />
                {t('pages.simulacao.acoes.lancar_proposta')}
              </Button>
              <Button
                variant="ghost"
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 text-start font-semibold"
                isLoading={duplicandoSimulacao}
                onClick={() => handleDuplicarSimulacao(simulacao.id)}
              >
                <Icon name="copy" size={16} />
                {t('pages.simulacao.acoes.duplicar_simulacao')}
              </Button>
              <Button
                variant="ghost"
                onClick={downloadPdfSimulacao(simulacao.id)}
                isLoading={baixandoPdf}
                loadingText={t('pages.simulacao.acoes.baixando_pdf')}
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 font-semibold"
              >
                <Icon name="import" size={16} />
                {t('pages.simulacao.acoes.baixar_pdf')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}

export const SimulacaoContent = {
  Provider: SimulacaoContentProvider,
  Header,
  EmpreendimentoSection,
  ValoresSimuladosSection,
}
